// hooks/useGroupedItems.js
import { useMemo, useRef } from "react";

/**
 * @typedef {Object} MapItem
 * @property {string} id
 * @property {string} compoundName
 * @property {string} reference
 * @property {string} fleetNumber
 * @property {string} category
 * @property {string} suppString1
 * @property {string} suppString2
 * @property {string} type
 * @property {string} icon
 * @property {string} positionType
 * @property {string} latitude
 * @property {string} longitude
 * @property {number} speed
 * @property {number} battery
 * @property {number} supply
 * @property {string} dateTime
 * @property {string} lastFix
 * @property {string} din3
 * @property {string} din4
 * @property {number} radius
 * @property {Object[]} polygon
 */

/**
 * @typedef {Object} ItemGroup
 * @property {string}    groupKey  - "lat,lng" rounded to 6dp
 * @property {MapItem[]} items     - all items at this position
 * @property {number}    count     - number of items in the group
 * @property {number}    lat       - representative lat for the group
 * @property {number}    lng       - representative lng for the group
 * @property {boolean}   showInfo  - flag to indicate InfoWindow should be displayed
 */

/**
 * Groups items by a lat/lng key rounded to 6 decimal places.
 * Pre-calculates count per group for use by the clusterer renderer.
 *
 * @param {MapItem[]} items
 * @returns {Map<string, ItemGroup>}
 */

export function useGroupedItems(items) {
  // const groupsRef = useRef(new Map());
  return useMemo(() => {
    const groups = new Map(); // groupsRef.current

    items.forEach((item) => {
      const key = `${Number(item.latitude).toFixed(6)},${Number(
        item.longitude
      ).toFixed(6)}`;

      if (groups.has(key)) {
        const group = groups.get(key);
        group.items.push(item);
        group.count = group.items.length;
      } else {
        groups.set(key, {
          groupKey: key,
          items: [item],
          count: 1,
          lat: item.latitude,
          lng: item.longitude,
          showInfo: false,
        });
      }
    });

    // We used the location as a key above, now we return the groups keyed by asset id
    const resultGroup = new Map();
    groups.forEach((v, k) => {
      resultGroup.set(v.items[0].id.toString(), v);
    });

    return resultGroup;
  }, [items]);
}
